import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

import SearchOutlined from '@ant-design/icons/SearchOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';

import { getSkillHierarchy } from 'services/api/skills';
import { parseLadder, weaveStatement } from 'sections/shared/skill-repo/paramEngine';

/**
 * Pick targets for a goal from the domain's Target Library (skill_target_bank),
 * grouped by category, searchable, multi-select. onAdd receives the chosen
 * targets ([{ name, materials }]).
 *
 * A name typed into the search box that matches no library target can be added as
 * a CUSTOM target ("+ Add …"), mirroring the custom-goal add on the OT / SLP
 * datasheets. Custom targets carry no materials and are created by name only.
 *
 * TWO STEPS WHEN A TARGET IS PARAMETERISED. A repository target may be authored
 * as a template — "Answer {1} questions using {2}" with ladders behind it — and
 * the value that makes it concrete is a decision about THIS child. So picking
 * such a target leads to a Configure step (exactly as GoalBuilderModal does for
 * goals) and the woven sentence, not the template, is what gets added. Targets
 * with no parameters skip the step entirely, so the common case is unchanged.
 */
const TargetPickerModal = ({ open, onClose, onAdd, domainCode, domainName }) => {
  const theme = useTheme();
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set()); // library target ids
  const [customNames, setCustomNames] = useState([]); // custom target names added this open
  // null = picking. Otherwise the configure step: one entry per chosen target
  // that has parameters, each carrying its parsed ladders and chosen values.
  const [configs, setConfigs] = useState(null);

  // In-flight guard for "Add selected targets" — the parent closes this modal
  // via state (an async re-render), so a fast double-click would fire onAdd
  // twice and create every chosen target twice. Mirrors GoalBuilderModal.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setConfigs(null);
    setSelected(new Set());
    setCustomNames([]);
    submittingRef.current = false;
    setSubmitting(false);
    // Refetch every open so newly approved targets appear. Cached data renders meanwhile.
    setLoading(hierarchy.length === 0);
    getSkillHierarchy()
      .then((t) => setHierarchy(Array.isArray(t) ? t : []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // All targets for this domain (its own + every target sub-domain), by category.
  const categories = useMemo(() => {
    // Legacy goal rows store the domain as "DOMAIN-MAND"; repo codes are bare
    // ("MAND") — normalize so both match.
    const code = String(domainCode || '').replace(/^DOMAIN-/i, '');
    const dom = hierarchy.find((d) => d.code === code);
    if (!dom) return [];
    // Only offer ACTIVE, APPROVED targets — retired/rejected and not-yet-approved
    // (pending) targets are excluded from the child target picker.
    const active = (arr) => (arr || []).filter((t) => t.is_active !== false && t.approval_status !== 'pending');
    const cats = [];
    if (active(dom.targets).length) cats.push({ name: 'General', targets: active(dom.targets) });
    (dom.sub_domains || []).forEach((s) => {
      if (active(s.targets).length) cats.push({ name: s.name, targets: active(s.targets) });
    });
    return cats;
  }, [hierarchy, domainCode]);

  const byId = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => c.targets.forEach((t) => m.set(t.id, t)));
    return m;
  }, [categories]);

  const q = search.trim().toLowerCase();
  const shown = categories
    .map((c) => ({ ...c, targets: c.targets.filter((t) => !q || t.name.toLowerCase().includes(q)) }))
    .filter((c) => c.targets.length);
  const total = shown.reduce((n, c) => n + c.targets.length, 0);

  // A trimmed search that exactly matches neither a library target nor an
  // already-added custom target can be added as a custom target.
  const typed = search.trim();
  const existsInLibrary = categories.some((c) => c.targets.some((t) => t.name.toLowerCase() === typed.toLowerCase()));
  const existsInCustom = customNames.some((n) => n.toLowerCase() === typed.toLowerCase());
  const canAddCustom = typed.length > 0 && !existsInLibrary && !existsInCustom;

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const addCustom = () => {
    if (!canAddCustom) return;
    setCustomNames((prev) => [...prev, typed]);
    setSearch('');
  };

  const removeCustom = (name) => setCustomNames((prev) => prev.filter((n) => n !== name));

  const totalSelected = selected.size + customNames.length;

  /** Ladder strings -> five editable specs, filled from the repository where it
   *  has something. Always five: the goal text can reference {1}..{5}, so a
   *  clinician has to be able to supply one the repository left blank. NA is the
   *  authored way of saying "no parameter" and reads as empty. */
  const specsFor = (target) => {
    const raws = target.parameters || [];
    return Array.from({ length: 5 }, (_, i) => {
      const raw = String(raws[i] || '').trim();
      const usable = raw && raw.toUpperCase() !== 'NA' ? raw : '';
      return { raw: usable, meta: parseLadder(usable), value: usable };
    });
  };

  /** Selected library targets, in pick order, with their parameter specs. */
  const chosenTargets = () =>
    [...selected]
      .map((id) => byId.get(id))
      .filter(Boolean)
      .map((t) => ({ id: t.id, name: t.name, materials: t.materials || [], specs: specsFor(t) }));

  // Step 1 -> 2. The Configure step always opens: a target is a template until
  // someone has looked at it, and it used to be added the moment it was picked
  // whenever the repository had left its parameters empty.
  const proceed = async () => {
    setConfigs(chosenTargets());
  };

  const setParamValue = (targetId, idx, value) =>
    setConfigs((prev) =>
      (prev || []).map((c) => (c.id === targetId ? { ...c, specs: c.specs.map((sp, i) => (i === idx ? { ...sp, value } : sp)) } : c))
    );

  const commit = async (configured) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const byIdConfigured = new Map((configured || []).map((c) => [c.id, c]));
      const chosen = [
        ...chosenTargets().map((t) => {
          const cfg = byIdConfigured.get(t.id);
          // The woven sentence is what the child's plan carries - a template with
          // "{1}" left in it would read as a placeholder on the data sheet.
          const name = cfg ? weaveStatement(cfg.name ?? t.name, cfg.specs).statement : t.name;
          // Carried alongside the woven title so the target can be reopened and
          // its parameters changed, rather than only re-read as a sentence.
          const parameters = (cfg ? cfg.specs : t.specs || []).map((sp) => (sp.value ?? '').toString().trim());
          // Both: the sentence for the plan, the template for the edit dialog.
          return { name, nameTemplate: cfg ? (cfg.name ?? t.name) : t.name, materials: t.materials, parameters };
        }),
        // Custom targets: name only, no materials.
        ...customNames.map((name) => ({ name, nameTemplate: name, materials: [], parameters: [] }))
      ];
      await onAdd(chosen);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Add targets · {domainName}
        <IconButton size="small" onClick={onClose}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 360 }}>
        {/* Step 1 — pick. Hidden once the Configure step opens so the two never stack. */}
        {configs ? null : loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder="Search targets or type a custom target…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                // Enter adds the typed text as a custom target when it matches none.
                if (e.key === 'Enter' && canAddCustom) {
                  e.preventDefault();
                  addCustom();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                )
              }}
            />

            {/* Add-custom affordance — mirrors the "+ Add …" option on the OT / SLP
                datasheets. Shown whenever the typed name matches no existing target. */}
            {canAddCustom && (
              <Chip
                icon={<PlusOutlined style={{ fontSize: '0.75rem' }} />}
                label={`Add "${typed}" as a custom target`}
                onClick={addCustom}
                variant="outlined"
                color="primary"
                sx={{ alignSelf: 'flex-start', fontWeight: 500 }}
              />
            )}

            {/* Custom targets already added this session — removable. */}
            {customNames.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
                >
                  Custom targets <span style={{ fontWeight: 400 }}>({customNames.length})</span>
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {customNames.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      onDelete={() => removeCustom(name)}
                      variant="filled"
                      color="primary"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15) }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {categories.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No targets exist yet for the <b>{domainName}</b> domain — you can add a custom target above, or author library targets in
                the Skill Repository.
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  {total} suggested target{total === 1 ? '' : 's'} under <b>{domainName}</b> — select any to add.
                </Typography>
                {shown.map((cat) => (
                  <Box key={cat.name}>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
                    >
                      {cat.name} <span style={{ fontWeight: 400 }}>({cat.targets.length})</span>
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                      {cat.targets.map((t) => {
                        const sel = selected.has(t.id);
                        return (
                          <Chip
                            key={t.id}
                            label={t.name}
                            size="small"
                            onClick={() => toggle(t.id)}
                            icon={sel ? <CheckOutlined style={{ fontSize: '0.7rem' }} /> : undefined}
                            variant={sel ? 'filled' : 'outlined'}
                            color={sel ? 'primary' : 'default'}
                            sx={{ ...(sel && { bgcolor: alpha(theme.palette.primary.main, 0.15) }) }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
                {total === 0 && !canAddCustom && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No targets match “{search}”.
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}

        {/* STEP 2 — make each parameterised target concrete for this child. */}
        {configs && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              These targets are written as templates. Choose the value for each parameter — the suggested steps are the author&apos;s, any
              value is allowed.
            </Typography>
            {configs.map((c) => {
              const preview = weaveStatement(c.name, c.specs).statement;
              return (
                <Box key={c.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={3}
                    label="Target"
                    value={c.name}
                    onChange={(e) => setConfigs((prev) => (prev || []).map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)))}
                    sx={{ mb: 1 }}
                  />
                  <Stack spacing={1.25}>
                    {c.specs.map((sp, idx) => (
                      <TextField
                        key={idx}
                        fullWidth
                        size="small"
                        multiline
                        maxRows={3}
                        label={`Parameter ${idx + 1}`}
                        value={sp.value}
                        onChange={(e) => setParamValue(c.id, idx, e.target.value)}
                      />
                    ))}
                  </Stack>
                  <Box sx={{ mt: 1.25, p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block' }}>
                      Will be added as
                    </Typography>
                    <Typography variant="body2">{preview}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
          {totalSelected} selected
        </Typography>
        <Box>
          {configs ? (
            <Button onClick={() => setConfigs(null)} sx={{ mr: 1 }} disabled={submitting}>
              Back
            </Button>
          ) : (
            <Button onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => (configs ? commit(configs) : proceed())}
            disabled={totalSelected === 0 || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? 'Adding…' : configs ? 'Add targets' : 'Add selected targets'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

TargetPickerModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  domainCode: PropTypes.string,
  domainName: PropTypes.string
};

export default TargetPickerModal;
